import React, { useEffect, useState, useMemo, useRef, useLayoutEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { Box, Typography, Button, CircularProgress, Divider, Stack, Chip } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import styles from "./AdminDetail.module.css";
import { useTranslation } from "react-i18next";
import headacheJsonData from "../../data/headache.json";
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const getStrokeCategory = (keyword: string): string => {
  if (keyword.includes("혈압") || keyword.includes("당뇨") || keyword.includes("심방세동") || 
      keyword.includes("콜레스테롤") || keyword.includes("가족력")) return "category_medical_history"; 
  return "category_basic"; 
};

const getStrokeSubCategory = (keyword: string): string => {
  if (keyword.includes("혈압")) return "고혈압";
  if (keyword.includes("당뇨")) return "당뇨";
  if (keyword.includes("심방세동")) return "심방세동";
  if (keyword.includes("콜레스테롤")) return "고지혈증";
  if (keyword.includes("가족력")) return "가족력";
  if (keyword.includes("음식") || keyword.includes("음료")) return "식습관";
  if (keyword.includes("운동") || keyword.includes("신체")) return "운동";
  if (keyword.includes("흡연") || keyword.includes("담배")) return "흡연";
  if (keyword.includes("음주") || keyword.includes("술")) return "음주";
  return "기타"; 
};

// --- [두통 전용 헬퍼] 질문 찾기 및 카테고리 분류 ---
const findQuestion = (id: string, questions: any[]): any => {
  for (const q of questions) {
    if (q.id === id) return q;
    if (q.followUpIfYes) {
      const result = findQuestion(id, q.followUpIfYes);
      if (result) return result;
    }
  }
  return undefined;
};

const getHeadacheCategory = (keyword: string): string => {
  if (/갑작|뻣뻣|구토|심해지는|아침에|기억력|성격|건망증|이상 행동|신체 약화|언어|신경|한쪽에|자세|기침|누워있을|무거운/.test(keyword)) return "category_red_flags";
  if (/발열|체중 감소|식욕|발진|관절통|여행|감염/.test(keyword)) return "category_systemic_symptoms";
  if (/빛|소리|메스꺼움|어두운|눈물|충혈|코막힘|같은 시간|눈 주변/.test(keyword)) return "category_headache_type";
  if (/진통제|생리|외상|암|면역/.test(keyword)) return "category_other_factors";
  return "category_other";
};

const getHeadacheCategoryPriority = (key: string) => {
  const priorities: Record<string, number> = { category_red_flags: 1, category_systemic_symptoms: 2, category_headache_type: 3, category_other_factors: 4, category_other: 99 };
  return priorities[key] || 99;
};

const AdminDetail: React.FC = () => {
  const { id, hospitalId } = useParams<{ id: string; hospitalId?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [masterData, setMasterData] = useState<any>(null);
  const [strokeData, setStrokeData] = useState<any>(null);
  const [cognitiveData, setCognitiveData] = useState<any>(null);
  const [headacheDataResult, setHeadacheDataResult] = useState<any>(null);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const reportWrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [loading]);

  const handleDownloadPdf = async () => {
    const container = contentRef.current;
    if (!container) return;

    setIsGeneratingPdf(true);
    const originalWidth = container.style.width;
    const originalOverflow = container.style.overflowY;
    
    try {
      container.style.width = '800px';
      container.style.overflowY = 'visible';
      document.body.classList.add('isPdfing');

      await new Promise((resolve) => setTimeout(resolve, 500));

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pdfWidth - (margin * 2);
      const contentHeight = pdfHeight - (margin * 2);

      // 개별 항목 단위로 쪼개진 모든 섹션을 가져옵니다.
      const elements = Array.from(container.querySelectorAll('[data-pdf-section="true"]'));
      let currentHeight = 0;

      for (let i = 0; i < elements.length; i++) {
        const el = elements[i] as HTMLElement;
        const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' } as any);
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        // 현재 페이지에 공간이 부족하면 다음 페이지로
        if (currentHeight + imgHeight > contentHeight) {
          pdf.addPage();
          currentHeight = 0;
        }

        pdf.addImage(imgData, 'JPEG', margin, margin + currentHeight, contentWidth, imgHeight);
        currentHeight += imgHeight + 2; // 항목 간 미세 간격
      }

      const totalPages = pdf.getNumberOfPages();
      for (let j = 1; j <= totalPages; j++) {
        pdf.setPage(j);
        pdf.setFontSize(10);
        pdf.setTextColor(150);
        pdf.text(`${j} / ${totalPages}`, pdfWidth / 2, pdfHeight - 5, { align: 'center' });
      }

      pdf.save(`BrainCheck_Report_${id}.pdf`);
    } catch (error) {
      console.error("PDF 생성 에러:", error);
      alert("PDF 저장 중 오류가 발생했습니다.");
    } finally {
      container.style.width = originalWidth;
      container.style.overflowY = originalOverflow;
      document.body.classList.remove('isPdfing');
      setIsGeneratingPdf(false);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      if (!id) return;
      try {
        // 1. 통합 리포트 마스터 데이터(reports) 호출
        const masterSnap = await getDoc(doc(db, "reports", id));
        
        if (masterSnap.exists()) {
          const data = masterSnap.data();
          setMasterData(data);

          // 2. 각 리포트 ID가 있을 경우 상세 데이터 병렬 호출
          const promises = [];
          if (data.strokeReportId) {
            promises.push(getDoc(doc(db, "sharedStrokeResults", data.strokeReportId)).then(s => setStrokeData(s.data())));
          }
          if (data.cognitiveReportId) {
            promises.push(getDoc(doc(db, "sharedCognitiveResults", data.cognitiveReportId)).then(s => setCognitiveData(s.data())));
          }
          if (data.headacheReportId) {
            promises.push(getDoc(doc(db, "sharedHeadacheResults", data.headacheReportId)).then(s => setHeadacheDataResult(s.data())));
          }
          
          await Promise.all(promises);
        }
      } catch (err) {
        console.error("데이터 통합 로드 실패:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [id]);

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleScroll = () => {
      contentElement.classList.remove(styles.scrollHidden);
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        contentElement.classList.add(styles.scrollHidden);
      }, 400); 
    };

    contentElement.addEventListener('scroll', handleScroll);
    contentElement.classList.add(styles.scrollHidden);

    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, [loading]);

  const getStatusColor = (count: number): "error" | "success" => {
    return count > 0 ? "error" : "success";
  };

  const groupedHeadacheFactors = useMemo(() => {
    const factorsToUse = headacheDataResult?.qaRiskFactors || headacheDataResult?.riskFactors;
    if (!factorsToUse) return new Map<string, any[]>();

    const categorized = factorsToUse.map((f: any) => {
      const qData = findQuestion(f.keyword, headacheJsonData.questions);
      let classificationKeyword = f.keyword;
      if (qData?.riskFactor) classificationKeyword = t(qData.riskFactor.keyword);
      else if (qData?.options) {
        const riskOpt = qData.options.find((opt: any) => opt.riskFactor);
        if (riskOpt) classificationKeyword = t(riskOpt.riskFactor.keyword);
      }
      const categoryKey = getHeadacheCategory(classificationKeyword);
      return { ...f, categoryKey, priority: getHeadacheCategoryPriority(categoryKey), displayQuestion: qData ? t(qData.questionTextKey || qData.text_key) : f.keyword };
    });

    categorized.sort((a: any, b: any) => a.priority - b.priority);
    const grouped = new Map<string, any[]>();
    categorized.forEach((f: any) => {
      if (!grouped.has(f.categoryKey)) grouped.set(f.categoryKey, []);
      grouped.get(f.categoryKey)?.push(f);
    });
    return grouped;
  }, [headacheDataResult, t]);

  const getCognitiveTestInfo = (testType: string) => {
    let typeKey = testType;
    if (testType === 'digit_span') typeKey = 'test1';
    if (testType === 'random_cube') typeKey = 'test2';
    if (testType === 'verbal_learning_test') typeKey = 'test3';
    if (testType === 'tower_of_london') typeKey = 'test4';

    switch (typeKey) {
      case "test1": return { title: t("test_type_test1", "숫자 순서 맞추기"), domain: t("domain_test1", "주의력 및 작업 기억"), desc: t("desc_test1", "청각적 정보를 단기적으로 기억하고 조작하는 능력을 평가합니다.") };
      case "test2": return { title: t("test_type_test2", "입체 도형 맞추기"), domain: t("domain_test2", "시공간 작업 기억"), desc: t("desc_test2", "시각적 정보의 위치와 깊이를 파악하는 능력을 평가합니다.") };
      case "test3": return { title: t("test_type_test3", "단어 기억 테스트"), domain: t("domain_test3", "언어적 기억력"), desc: t("desc_test3", "새로운 언어 정보를 학습하고 기억해내는 능력을 평가합니다.") };
      case "test4": return { title: t("test_type_test4", "하노이의 탑"), domain: t("domain_test4", "실행 기능 및 계획력"), desc: t("desc_test4", "목표 달성을 위해 순서를 계획하고 문제를 해결하는 전두엽 기능을 평가합니다.") };
      default: return { title: testType, domain: "", desc: "" };
    }
  };

  const groupedStrokeFactors = useMemo(() => {
    if (!strokeData) return null;
    const factors = strokeData.qaRiskFactors || strokeData.riskFactors || [];
    const categoryMap = new Map<string, Map<string, any[]>>();

    factors.forEach((f: any) => {
      const cat = getStrokeCategory(f.keyword);
      const sub = getStrokeSubCategory(f.keyword);
      if (!categoryMap.has(cat)) categoryMap.set(cat, new Map());
      if (!categoryMap.get(cat)?.has(sub)) categoryMap.get(cat)?.set(sub, []);
      categoryMap.get(cat)?.get(sub)?.push(f);
    });
    return categoryMap;
  }, [strokeData]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <div className={styles.container}>
      <div className={styles.reportPaper}>
        {/* 상단 액션바 */}
        <Box className={styles.actionBar}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>목록으로</Button>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />} 
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
          >
            {isGeneratingPdf ? "생성 중..." : "PDF 저장 / 인쇄"}
          </Button>
        </Box>

        <div className={styles.contentScroll} ref={contentRef}>
          <div className={styles.innerWrapper}>
            <header className={styles.header} data-pdf-section="true">
              <Typography variant="h4" className={styles.mainTitle}>BrainCheck 통합 리포트</Typography>
              <Box className={styles.metaGrid}>
                <div className={styles.metaItem}><strong>병원:</strong> {masterData?.hospitalId || '일반'}</div>
                <div className={styles.metaItem}><strong>환자 ID:</strong> {id}</div>
              </Box>
            </header>

            <Stack spacing={{ xs: 4, sm: 8 }}>
              {/* 1. 뇌졸중 리포트 섹션 */}
              {groupedStrokeFactors && (
                <section className={styles.section}>
                  <div className={styles.sectionHeader} data-pdf-section="true">
                    <h2 className={styles.reportSectionTitle}>뇌졸중 위험요소 검사 결과</h2>
                    <Chip 
                      label={`${strokeData.score}개 위험요소 식별`} 
                      color={getStatusColor(strokeData.score)} 
                      className={styles.statusChip}
                    />
                  </div>

                  {Array.from(groupedStrokeFactors.entries()).map(([catKey, subGroups]) => (
                    <div key={catKey} className={styles.categoryWrapper}>
                      <h3 className={styles.categoryTitle} data-pdf-section="true">
                        {catKey === 'category_basic' ? "기본 건강 수칙" : "과거력 및 기저질환"}
                      </h3>
                      {Array.from(subGroups.entries()).map(([subKey, factors]) => (
                        <div key={subKey} className={styles.subCategoryGroup}>
                          <h4 className={styles.subCategoryTitle} data-pdf-section="true">{subKey}</h4>
                          <div className={styles.qaList}>
                            {factors.map((f, i) => (
                              <div key={i} className={styles.qaItem} data-pdf-section="true">
                                <span className={styles.qaQuestion}>{f.keyword}</span>
                                <span className={styles.qaAnswer}>{f.description}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </section>
              )}

              {/* 2. 인지기능 리포트 섹션 */}
              {cognitiveData && (
                <>
                  <div className={styles.sectionHeader} data-pdf-section="true" style={{ marginTop: '40px' }}>
                    <h2 className={styles.reportSectionTitle}>인지 기능 평가</h2>
                    <Chip 
                      label={cognitiveData.totalRiskCount > 0 ? `${cognitiveData.totalRiskCount}개 영역 주의` : "모든 영역 정상"} 
                      color={getStatusColor(cognitiveData.totalRiskCount)} 
                      className={styles.statusChip}
                    />
                  </div>

                  {/* 인지 기능 위험 요인 분석 */}
                  {cognitiveData.riskFactors && cognitiveData.riskFactors.length > 0 && (
                    <div className={styles.riskAnalysisBox} data-pdf-section="true">
                      <h4 className={styles.riskAnalysisTitle}>인지 영역 위험 요인 분석</h4>
                      {cognitiveData.riskFactors.map((rf: any, idx: number) => (
                        <div key={idx} className={styles.riskAnalysisItem}>
                          <span className={styles.riskKeyword}>{rf.keyword}</span>
                          <span className={styles.riskDesc}>{rf.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className={styles.testList}>
                    {cognitiveData.testScores.map((test: any, index: number) => {
                      const info = getCognitiveTestInfo(test.testType);
                      const percent = Math.min((test.score / test.totalCount) * 100, 100);
                      return (
                        <div key={index} className={styles.testCard} data-pdf-section="true">
                          <div className={styles.cardHeader}>
                            <h4 className={styles.testTitle}>{info.title}</h4>
                            {info.domain && <span className={styles.domainBadge}>{info.domain}</span>}
                          </div>
                          <p className={styles.testDesc}>{info.desc}</p>
                          <div className={styles.scoreContainer}>
                            <div className={styles.scoreRow}>
                               <span className={styles.scoreLabel}>수행 점수</span>
                               <span className={styles.scoreValue}>{test.score} / {test.totalCount}</span>
                            </div>
                            <div className={styles.progressBarBg}>
                               <div 
                                 className={`${styles.progressBarFill} ${test.isRisk ? styles.fillRed : styles.fillGreen}`}
                                 style={{ width: `${percent}%` }}
                               ></div>
                            </div>
                            <div className={styles.statusRow}>
                               <span>판정</span>
                               <span className={test.isRisk ? styles.textRed : styles.textGreen}>
                                 {test.isRisk ? "주의 필요" : "정상 범위"}
                               </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* 3. 두통 리포트 섹션 */}
              {headacheDataResult && (
                <>
                  <div className={styles.sectionHeader} data-pdf-section="true" style={{ marginTop: '40px' }}>
                    <h2 className={styles.reportSectionTitle}>두통 위험요소 검사</h2>
                    <Chip 
                      label={`${headacheDataResult.score}개 위험요소 식별`} 
                      color={getStatusColor(headacheDataResult.score)} 
                      className={styles.statusChip}
                    />
                  </div>

                  {Array.from(groupedHeadacheFactors.entries()).map(([catKey, factors]) => (
                    <div key={catKey}>
                      <h3 className={styles.categoryTitle} data-pdf-section="true">
                        {t(`headache_report.${catKey}_title`, catKey)}
                      </h3>
                      <div className={styles.qaList}>
                        {factors.map((f, i) => (
                          <div key={i} className={styles.qaItem} data-pdf-section="true">
                            <span className={styles.qaQuestion}>{f.displayQuestion}</span>
                            <span className={styles.qaAnswer}>{f.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {groupedHeadacheFactors.size === 0 && (
                    <p className={styles.noRiskMessage}>식별된 두통 위험 요인이 없습니다.</p>
                  )}
                </>
              )}
            </Stack>

            <footer className={styles.footer}>
              BrainCheck © 2026 SHMD. All rights reserved.
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDetail;